import { useQuery } from '@tanstack/react-query';
import {
  AlertCircle,
  ArrowDown,
  ArrowUp,
  ChevronLeft,
  ChevronRight,
  Coins,
  Download,
  Gamepad2,
  Target,
  Telescope,
  TrendingUp,
} from 'lucide-react';
import { useMemo, useState, type ReactNode } from 'react';
import { Explain } from '@/components/Explain';
import { CopyId } from '@/components/primitives';
import { cn } from '@/lib/cn';
import type { Bootstrap, LookupParams } from '@/lib/bootstrap';
import { formatTime, parseClickhouseTime, type TimeZoneMode } from '@/lib/format';
import type { GameBetSummaryRow, GameReportInput, GameReportSort } from '@/types';
import { ApiError, fetchClients, fetchGames } from '@/features/bet-inspection/lib/api';
import { fetchReportCurrencies } from '@/features/player-reports/lib/api';
import { defaultRange, toClickhouseUtc } from '@/features/player-reports/lib/time';
import { FilterBar, type GameReportFilters } from './components/FilterBar';
import { fetchGameReport } from './lib/api';
import { downloadCsv, gameReportFilename, toCsv } from './lib/csv';

const PAGE_SIZE = 100;

/**
 * Game Reports — per-BET history for one game over a period.
 *
 * The unit is the bet, not the transaction: stake, payout, whether it was
 * reversed. Reads the settled copy, so it is authoritative and about an hour
 * behind.
 *
 * ⚠️ Row amounts are NOT converted — a bet happened in one currency, and
 * showing it in another would show the player a figure they never saw. Only the
 * totals convert, and they say which currency they are in.
 */
export function GameReports({
  boot,
  tz,
  base,
  onOpenBet,
}: {
  boot: Bootstrap;
  tz: TimeZoneMode;
  base: string;
  onOpenBet: (lookup: LookupParams) => void;
}) {
  const [filters, setFilters] = useState<GameReportFilters>(defaultFilters);
  const [applied, setApplied] = useState<GameReportFilters | null>(null);
  const [sort, setSort] = useState<GameReportSort>('started_at');
  const [dir, setDir] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(0);

  const clients = useQuery({
    queryKey: ['clients', boot.api],
    queryFn: ({ signal }) => fetchClients({ api: boot.api!, token: boot.token, signal }),
    enabled: Boolean(boot.api),
    staleTime: 5 * 60_000,
    retry: 1,
  });

  const games = useQuery({
    queryKey: ['games', boot.api, filters.psid],
    queryFn: ({ signal }) =>
      fetchGames({ api: boot.api!, token: boot.token, playServiceId: filters.psid, signal }),
    enabled: Boolean(boot.api && filters.psid),
    staleTime: 5 * 60_000,
  });

  const currencies = useQuery({
    queryKey: ['report-currencies', boot.api, filters.psid],
    queryFn: ({ signal }) =>
      fetchReportCurrencies({
        api: boot.api!,
        token: boot.token,
        playServiceId: filters.psid,
        signal,
      }),
    enabled: Boolean(boot.api && filters.psid),
    staleTime: 5 * 60_000,
  });

  const report = useQuery({
    queryKey: ['game-report', boot.api, applied, sort, dir, base, page],
    queryFn: ({ signal }) =>
      fetchGameReport({
        api: boot.api!,
        token: boot.token,
        input: { ...toInput(applied!, base), sort, dir, limit: PAGE_SIZE, offset: page * PAGE_SIZE },
        signal,
      }),
    enabled: Boolean(boot.api && applied),
  });

  const rows = report.data?.rows ?? [];
  const totals = report.data?.totals;
  const resolved = report.data?.resolved;
  const unit = report.data?.unit ?? applied?.currency ?? base;

  const csv = useMemo(() => (rows.length ? toCsv(rows) : ''), [rows]);

  const clientName = useMemo(
    () =>
      (applied &&
        (clients.data ?? []).find((c) => c.play_service_id === applied.psid)?.client_name) ||
      applied?.psid ||
      '',
    [clients.data, applied],
  );

  // Prefer the game the SERVER resolved — a combined round id can name a game
  // other than the one in the picker.
  const gameName = useMemo(() => {
    const uuid = resolved?.game_uuid ?? applied?.gameUuid;
    return (games.data ?? []).find((g) => g.game_uuid === uuid)?.name || uuid || '';
  }, [games.data, resolved, applied]);

  const search = () => {
    setPage(0);
    setApplied({ ...filters });
  };

  return (
    <>
      <div className="sticky top-[41px] z-30 border-b border-hairline bg-bg/95 backdrop-blur">
        <FilterBar
          value={filters}
          onChange={(patch) => setFilters((f) => ({ ...f, ...patch }))}
          onSearch={search}
          onReset={() => {
            setFilters(defaultFilters());
            setApplied(null);
            setPage(0);
          }}
          loading={report.isFetching}
          clients={clients.data ?? []}
          clientsLoading={clients.isLoading}
          clientsError={clients.isError}
          currencies={currencies.data ?? []}
          api={boot.api!}
          token={boot.token}
          tz={tz}
          base={base}
        />
      </div>

      {report.isError && <Failure error={report.error as Error} />}
      {!applied && !report.isError && <Idle />}

      {applied && !report.isError && (
        <main className="rise">
          {/* A rejected combined id returns nothing on purpose — say so loudly,
              or an empty table reads as "this bet does not exist". */}
          {resolved?.error && (
            <div className="flex gap-2.5 border-b border-hairline border-l-2 border-l-warn bg-warn-bg px-4 py-2.5">
              <AlertCircle size={14} className="mt-[2px] shrink-0 text-warn" />
              <p className="text-[12.5px] leading-relaxed text-text">{resolved.error}</p>
            </div>
          )}

          {resolved?.from_combined && !resolved.error && (
            <div className="flex flex-wrap items-center gap-x-5 gap-y-1 border-b border-hairline bg-surface-sunken px-4 py-1.5 text-[11.5px] text-text-dim">
              <span className="eyebrow">from combined id</span>
              <span>
                game <CopyId value={resolved.game_uuid ?? ''} truncate />
              </span>
              <span>
                bet <CopyId value={resolved.bet_id ?? ''} />
              </span>
            </div>
          )}

          <KpiRow
            totals={totals}
            unit={unit}
            converted={report.data?.converted ?? false}
            onPage={rows.length}
            loading={report.isFetching}
          />

          {report.data?.unconverted?.length ? (
            <div className="flex items-center gap-2 border-b border-hairline bg-warn-bg/40 px-4 py-1.5 text-[12px] text-text-dim">
              <span className="text-warn">▲</span>
              Excluded from the totals — no exchange rate:{' '}
              <span className="font-mono text-warn">{report.data.unconverted.join(', ')}</span>
            </div>
          ) : null}

          <section className="border-b border-hairline">
            <div className="flex flex-wrap items-center gap-3 px-4 py-2">
              <span className="font-condensed text-[12px] font-semibold uppercase tracking-wider text-text">
                Bets
              </span>
              <span className="font-mono text-[11px] text-text-dim">
                {report.isFetching
                  ? 'loading…'
                  : `${rows.length ? page * PAGE_SIZE + 1 : 0}–${page * PAGE_SIZE + rows.length} of ${totals?.bets ?? 0}`}
              </span>

              <div className="ml-auto flex items-center gap-1.5">
                <button
                  type="button"
                  disabled={page === 0 || report.isFetching}
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  className="inline-flex items-center border border-hairline px-1.5 py-1 text-text-dim hover:border-hairline-strong hover:text-text disabled:opacity-30"
                >
                  <ChevronLeft size={13} />
                </button>
                <button
                  type="button"
                  disabled={!report.data?.truncated || report.isFetching}
                  onClick={() => setPage((p) => p + 1)}
                  className="inline-flex items-center border border-hairline px-1.5 py-1 text-text-dim hover:border-hairline-strong hover:text-text disabled:opacity-30"
                >
                  <ChevronRight size={13} />
                </button>
                <button
                  type="button"
                  disabled={!csv}
                  onClick={() =>
                    downloadCsv(
                      gameReportFilename(clientName, gameName, toClickhouseUtc(applied.from)),
                      csv,
                    )
                  }
                  title="Export the bets on this page"
                  className="inline-flex items-center gap-1.5 border border-hairline px-2.5 py-1 font-condensed text-[10px] font-semibold uppercase tracking-wider text-text-dim hover:border-hairline-strong hover:text-text disabled:opacity-40"
                >
                  <Download size={12} /> Export CSV
                </button>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full border-collapse whitespace-nowrap text-[12px]">
                <thead>
                  <tr className="border-b border-hairline bg-surface-sunken">
                    <Th col="started_at" sort={sort} dir={dir} onSort={setSort} onDir={setDir} align="left">
                      Started
                    </Th>
                    <Th col="bet_id" sort={sort} dir={dir} onSort={setSort} onDir={setDir} align="left">
                      <Explain term="bet_id">Bet</Explain>
                    </Th>
                    <Th col="player_id" sort={sort} dir={dir} onSort={setSort} onDir={setDir} align="left">
                      <Explain term="player_id">Player</Explain>
                    </Th>
                    <Th align="left">
                      <Explain term="round_id">Round</Explain>
                    </Th>
                    <Th col="bet" sort={sort} dir={dir} onSort={setSort} onDir={setDir}>Bet</Th>
                    <Th col="win" sort={sort} dir={dir} onSort={setSort} onDir={setDir}>Win</Th>
                    <Th align="right">Rollback</Th>
                    <Th col="ggr" sort={sort} dir={dir} onSort={setSort} onDir={setDir}>GGR</Th>
                    <Th align="left">Cur</Th>
                    <Th align="right">Trx</Th>
                    <Th />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <Row key={r.combined_round_id} row={r} tz={tz} psid={applied.psid} onOpenBet={onOpenBet} />
                  ))}
                  {!rows.length && !report.isFetching && !resolved?.error && (
                    <tr>
                      <td colSpan={11} className="px-3 py-6 text-center text-[12px] text-text-dim">
                        No bets for this game, filters and period.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>

          <footer className="px-4 py-6 text-center text-[11px] leading-relaxed text-text-faint">
            ggr = bet − win − rollback · hit rate = share of bets that paid anything · times are UTC
            <br />
            Bet amounts are shown in each bet's own currency and are never converted; only the
            totals are.
          </footer>
        </main>
      )}
    </>
  );
}

function Row({
  row,
  tz,
  psid,
  onOpenBet,
}: {
  row: GameBetSummaryRow;
  tz: TimeZoneMode;
  psid: string;
  onOpenBet: (lookup: LookupParams) => void;
}) {
  const started = parseClickhouseTime(row.started_at);
  const won = Number(row.win) > 0;

  return (
    <tr
      className={cn(
        'border-b border-hairline/50 last:border-0 hover:bg-surface-raised/50',
        row.rolled_back ? 'bg-warn-bg/30' : '',
      )}
    >
      <td className="tnum px-3 py-1.5 font-mono text-[11.5px] text-text-dim">
        {started ? formatTime(started, tz, true) : '—'}
      </td>
      <td className="px-3 py-1.5">
        <CopyId value={row.bet_id} className="font-semibold" />
        {row.rolled_back ? (
          <span className="ml-2 border border-warn/40 px-1 font-condensed text-[9px] uppercase tracking-wider text-warn">
            rolled back
          </span>
        ) : null}
      </td>
      <td className="px-3 py-1.5">
        <CopyId value={row.player_id} />
      </td>
      <td className="tnum px-3 py-1.5 font-mono text-[11.5px] text-text-dim">{row.round_id}</td>
      <td className="px-3 py-1.5 text-right"><Amount value={row.bet} /></td>
      <td className="px-3 py-1.5 text-right">
        <Amount value={row.win} tone={won ? 'ok' : undefined} />
      </td>
      <td className="px-3 py-1.5 text-right">
        <Amount value={row.rollback_amount} tone={Number(row.rollback_amount) > 0 ? 'warn' : undefined} />
      </td>
      <td className="px-3 py-1.5 text-right">
        <Amount value={row.ggr} tone={Number(row.ggr) < 0 ? 'warn' : undefined} />
      </td>
      {/* Beside every amount, because these rows are not converted. */}
      <td className="px-3 py-1.5 font-mono text-[11px] text-text-faint">{row.currency}</td>
      <td className="tnum px-3 py-1.5 text-right font-mono text-text-dim">{row.transactions}</td>
      <td className="px-3 py-1.5 text-right">
        <button
          type="button"
          onClick={() =>
            onOpenBet({ play_service_id: psid, game_uuid: row.game_uuid, bet_id: row.bet_id })
          }
          title="Open this bet in Bet Inspection"
          className="inline-flex items-center gap-1 border border-hairline px-1.5 py-[2px] font-condensed text-[10px] uppercase tracking-wider text-text-dim hover:border-accent/40 hover:text-accent"
        >
          <Telescope size={11} /> inspect
        </button>
      </td>
    </tr>
  );
}

function KpiRow({
  totals,
  unit,
  converted,
  onPage,
  loading,
}: {
  totals?: {
    bets: number;
    players: number;
    bet: string;
    win: string;
    ggr: string;
    hit_rate: number;
  };
  unit: string;
  converted: boolean;
  onPage: number;
  loading: boolean;
}) {
  return (
    <section className="border-b border-hairline bg-surface">
      <div className="flex flex-wrap items-stretch divide-x divide-hairline">
        <Kpi
          label="Bets"
          icon={<Gamepad2 size={12} />}
          value={totals ? String(totals.bets) : '—'}
          sub={`${onPage} on this page`}
          loading={loading}
        />
        <Kpi
          label="Total Bet"
          icon={<Coins size={12} />}
          value={totals ? formatAmount(totals.bet) : '—'}
          sub={totals ? `${totals.players} unique players` : ''}
          currency={unit}
          loading={loading}
        />
        <Kpi
          label="Total Win"
          value={totals ? formatAmount(totals.win) : '—'}
          sub={totals ? `${formatPct(totals.hit_rate)} hit rate` : ''}
          currency={unit}
          loading={loading}
        />
        <Kpi
          label="GGR"
          icon={<TrendingUp size={12} />}
          value={totals ? formatAmount(totals.ggr) : '—'}
          sub="bet − win − rollback"
          currency={unit}
          loading={loading}
        />
        <div className="ml-auto flex items-center gap-1.5 px-4">
          {converted && (
            <span
              title={`Totals converted to ${unit} using the platform's exchange rates — approximate. Each bet row keeps its own currency.`}
              className="inline-flex items-center gap-1 border border-info/40 bg-info-bg px-2 py-0.5 font-condensed text-[10px] font-semibold uppercase tracking-wider text-info"
            >
              <Target size={10} /> totals → {unit}
            </span>
          )}
          <span
            title="Settled copy — authoritative, about an hour behind"
            className="inline-flex items-center border border-ok/40 bg-ok-bg px-2 py-0.5 font-condensed text-[10px] font-semibold uppercase tracking-wider text-ok"
          >
            settled
          </span>
        </div>
      </div>
    </section>
  );
}

function Kpi({
  label,
  value,
  sub,
  currency,
  icon,
  loading,
}: {
  label: string;
  value: string;
  sub?: string;
  currency?: string;
  icon?: ReactNode;
  loading: boolean;
}) {
  return (
    <div className="min-w-[180px] px-4 py-2.5">
      <div className="eyebrow mb-1 flex items-center gap-1.5">
        {icon}
        {label}
      </div>
      <div
        className={cn('tnum font-mono text-[19px] font-semibold text-text', loading && 'opacity-50')}
      >
        {value}
        {currency && value !== '—' && (
          <span className="ml-1 text-[11px] font-normal text-text-faint">{currency}</span>
        )}
      </div>
      {sub && <div className="mt-0.5 text-[11px] text-text-dim">{sub}</div>}
    </div>
  );
}

function Th({
  col,
  sort,
  dir,
  onSort,
  onDir,
  align = 'right',
  children,
}: {
  col?: GameReportSort;
  sort?: GameReportSort;
  dir?: 'asc' | 'desc';
  onSort?: (c: GameReportSort) => void;
  onDir?: (d: 'asc' | 'desc') => void;
  align?: 'left' | 'right';
  children?: ReactNode;
}) {
  // Columns the server has no sort key for stay inert rather than pretending.
  if (!col || !onSort || !onDir) {
    return (
      <th className={cn('px-3 py-1.5', align === 'right' ? 'text-right' : 'text-left')}>
        <span className="eyebrow text-text-dim">{children}</span>
      </th>
    );
  }

  const active = sort === col;
  return (
    <th className={cn('px-3 py-1.5', align === 'right' ? 'text-right' : 'text-left')}>
      <button
        type="button"
        onClick={() => {
          if (active) onDir(dir === 'asc' ? 'desc' : 'asc');
          else {
            onSort(col);
            onDir('desc');
          }
        }}
        className={cn(
          'eyebrow inline-flex items-center gap-1 hover:text-text',
          active ? 'text-accent' : 'text-text-dim',
        )}
      >
        {children}
        {active ? dir === 'asc' ? <ArrowUp size={10} /> : <ArrowDown size={10} /> : <span className="w-[10px]" />}
      </button>
    </th>
  );
}

/** 2dp for reading, the exact stored value on hover — never destroy precision. */
function Amount({ value, tone }: { value: string; tone?: 'ok' | 'warn' }) {
  const n = Number(value);
  return (
    <span
      title={value}
      className={cn(
        'tnum cursor-help font-mono',
        tone === 'warn' ? 'text-warn' : tone === 'ok' ? 'text-ok' : 'text-text',
      )}
    >
      {Number.isFinite(n) ? formatAmount(value) : value}
    </span>
  );
}

function formatAmount(value: string): string {
  const n = Number(value);
  if (!Number.isFinite(n)) return value;
  return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function formatPct(v: number): string {
  if (!Number.isFinite(v)) return '—';
  return `${(v * 100).toFixed(1)}%`;
}

/** The query is always UTC. Blank fields are omitted, never sent as ''. */
function toInput(f: GameReportFilters, base: string): GameReportInput {
  const combined = f.combinedRoundId.trim();
  return {
    play_service_id: f.psid,
    ...(combined ? { combined_round_id: combined } : {}),
    ...(f.gameUuid ? { game_uuid: f.gameUuid } : {}),
    ...(!combined && f.betId.trim() ? { bet_id: f.betId.trim() } : {}),
    ...(f.roundId.trim() ? { round_id: f.roundId.trim() } : {}),
    ...(f.playerId.trim() ? { player_id: f.playerId.trim() } : {}),
    ...(f.currency ? { currency: f.currency } : { base }),
    from: toClickhouseUtc(f.from),
    to: toClickhouseUtc(f.to),
  };
}

function defaultFilters(): GameReportFilters {
  const { from, to } = defaultRange();
  return {
    psid: '',
    gameUuid: '',
    combinedRoundId: '',
    roundId: '',
    playerId: '',
    betId: '',
    currency: '',
    from,
    to,
  };
}

function Idle() {
  return (
    <div className="instrument-bg flex min-h-[40vh] flex-col items-center justify-center gap-3 px-6 text-center">
      <Gamepad2 size={22} className="text-text-faint" />
      <p className="max-w-[480px] text-[13px] leading-relaxed text-text-dim">
        Per-bet history for one game: stake, payout and whether it was reversed, bet by bet.
      </p>
      <p className="max-w-[480px] text-[12px] leading-relaxed text-text-faint">
        Pick a project and a game — or paste a{' '}
        <Explain term="combined_round_id">combined round id</Explain> straight from a ticket, which
        fills in both.
      </p>
    </div>
  );
}

function Failure({ error }: { error: Error }) {
  const status = error instanceof ApiError ? error.status : 0;
  return (
    <div className="flex min-h-[30vh] items-start justify-center px-6 pt-12">
      <div className="flex max-w-[520px] gap-3 border border-crit/40 bg-crit-bg p-4">
        <AlertCircle size={16} className="mt-[2px] shrink-0 text-crit" />
        <div>
          <div className="mb-1 font-condensed text-[11px] font-semibold uppercase tracking-wider text-crit">
            {status === 403 ? 'Not permitted' : 'Report failed'}
          </div>
          <p className="text-[12.5px] leading-relaxed text-text">{error.message}</p>
        </div>
      </div>
    </div>
  );
}
